import { useTranslation } from 'react-i18next'
import { Phone01Icon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { Tooltip, TooltipTrigger } from '@/components/ui/Tooltip'
import { isWhatsAppOrigin, parseConversationMeta } from '@/lib/conversationChannel'
import { cn } from '@/lib/utils'

interface ChannelIconsProps {
  meta?: string
  twilioId?: string
  className?: string
}

// WhatsApp Icon Component
export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  )
}

// WhatsApp with reply indicator (two-way arrows in blue circle)
function WhatsAppIconWithReply({ className }: { className?: string }) {
  return (
    <div className="relative inline-flex items-center justify-center">
      <WhatsAppIcon className={className} />
      {/* Blue circle with two-way arrow indicator for sent & received */}
      <div className="absolute -bottom-0.5 -right-0.5 flex h-[11px] w-[11px] items-center justify-center rounded-full border border-white bg-[#2196f3]">
        <svg
          className="h-[7px] w-[7px] text-white"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Left arrow */}
          <path d="M5 12H19" />
          <path d="M5 12L9 8" />
          <path d="M5 12L9 16" />
          {/* Right arrow */}
          <path d="M19 12L15 8" />
          <path d="M19 12L15 16" />
        </svg>
      </div>
    </div>
  )
}

export function ChannelIcons({ meta, twilioId, className }: ChannelIconsProps) {
  const { t } = useTranslation()

  const whatsappMeta = parseConversationMeta(meta)?.whatsapp_messages
  const hasInbound = whatsappMeta?.inbound === true
  const hasOutbound = whatsappMeta?.outbound === true

  // WhatsApp-started conversations don't show the phone-icon since they never
  // touched the phone channel. We treat any wa-origin as having an inbound for
  // icon purposes (so the reply-indicator is shown) even before meta is
  // populated; an inbound-WhatsApp follow-up on a phone call still keeps the
  // phone-icon since the conversation did originate on the phone.
  const isWaOrigin = isWhatsAppOrigin({ twilio_id: twilioId })
  const hasAnyWhatsapp = isWaOrigin || hasInbound || hasOutbound
  const hasInboundWhatsapp = hasInbound || isWaOrigin

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {!isWaOrigin && (
        <TooltipTrigger>
          <Button color="tertiary" className="h-6 w-6 p-0" aria-label={t('resources.conversations.channels.phone')} iconLeading={Phone01Icon} />
          <Tooltip>{t('resources.conversations.channels.phone')}</Tooltip>
        </TooltipTrigger>
      )}
      {hasAnyWhatsapp && (
        <TooltipTrigger>
          <Button
            color="tertiary"
            className="h-6 w-6 p-0"
            aria-label={
              hasInboundWhatsapp
                ? t('resources.conversations.channels.whatsapp_both')
                : t('resources.conversations.channels.whatsapp_sent')
            }
          >
            {hasInboundWhatsapp ? (
              <WhatsAppIconWithReply className="h-4 w-4 text-[#25D366]" />
            ) : (
              <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
            )}
          </Button>
          <Tooltip>
            {hasInboundWhatsapp
              ? t('resources.conversations.channels.whatsapp_both')
              : t('resources.conversations.channels.whatsapp_sent')}
          </Tooltip>
        </TooltipTrigger>
      )}
    </div>
  )
}

export default ChannelIcons
